import { Injectable, inject } from '@angular/core';
import { Auth, authState, User } from '@angular/fire/auth';
import { Firestore } from '@angular/fire/firestore';
import { doc, getDoc, setDoc, updateDoc } from 'firebase/firestore';
import { docData } from 'rxfire/firestore';
import { Observable, BehaviorSubject, of } from 'rxjs';
import { switchMap, tap, shareReplay, catchError } from 'rxjs/operators';
import {
  UserProfile,
  SubscriptionStatus,
  createDefaultUserProfile,
  hasPremiumAccess,
} from '../../models/user-profile';

@Injectable({
  providedIn: 'root',
})
export class UserService {
  private firestore = inject(Firestore);
  private auth = inject(Auth);

  /** Observable of the current Firebase auth user */
  public user$ = authState(this.auth);

  /** Current user's UID */
  public userId: string | null = null;

  /** BehaviorSubject to track premium access status */
  private hasPremiumAccessSubject = new BehaviorSubject<boolean>(false);
  public hasPremiumAccess$ = this.hasPremiumAccessSubject.asObservable();

  /** Observable of the current user's profile */
  public userProfile$: Observable<UserProfile | null>;

  /** Cached user profile for synchronous access */
  private cachedProfile: UserProfile | null = null;

  constructor() {
    // Set up user profile observable that follows auth state
    this.userProfile$ = this.user$.pipe(
      switchMap((user) => {
        if (user) {
          this.userId = user.uid;
          return this.getUserProfile(user.uid);
        } else {
          this.userId = null;
          this.cachedProfile = null;
          return of(null);
        }
      }),
      tap({
        next: (profile) => {
          this.cachedProfile = profile;
          this.hasPremiumAccessSubject.next(hasPremiumAccess(profile));
        },
        error: (error) => {
          // Handle Firestore permission errors gracefully
          console.error('Error fetching user profile:', error);
          this.cachedProfile = null;
          this.hasPremiumAccessSubject.next(false);
        }
      }),
      shareReplay(1)
    );

    // Subscribe to keep the observable hot
    this.userProfile$.subscribe({
      error: (error) => console.error('User profile subscription error:', error)
    });
  }

  /**
   * Get user profile document from Firestore
   */
  getUserProfile(userId: string): Observable<UserProfile | null> {
    const userDocRef = doc(this.firestore, `users/${userId}`);
    return (docData(userDocRef) as Observable<UserProfile | null>).pipe(
      catchError((error) => {
        console.error('Firestore permission error for user profile:', error);
        return of(null);
      })
    );
  }

  /**
   * Get user profile once (not as observable)
   */
  async getUserProfileOnce(userId: string): Promise<UserProfile | null> {
    const userDocRef = doc(this.firestore, `users/${userId}`);
    const docSnap = await getDoc(userDocRef);
    return docSnap.exists() ? (docSnap.data() as UserProfile) : null;
  }

  /**
   * Create a new user profile document
   * Called when a new user signs up
   */
  async createUserProfile(user: User): Promise<UserProfile> {
    const profile = createDefaultUserProfile(user.uid, user.email || undefined);
    const userDocRef = doc(this.firestore, `users/${user.uid}`);
    await setDoc(userDocRef, profile);
    return profile;
  }

  /**
   * Create or get existing user profile
   * Ensures a profile exists for the user
   */
  async ensureUserProfile(user: User): Promise<UserProfile> {
    const existingProfile = await this.getUserProfileOnce(user.uid);
    if (existingProfile) {
      return existingProfile;
    }
    return this.createUserProfile(user);
  }

  /**
   * Update user profile with partial data
   */
  async updateUserProfile(userId: string, updates: Partial<UserProfile>): Promise<void> {
    const userDocRef = doc(this.firestore, `users/${userId}`);
    await updateDoc(userDocRef, updates);
  }

  /**
   * Update subscription status
   */
  async updateSubscriptionStatus(userId: string, status: SubscriptionStatus): Promise<void> {
    await this.updateUserProfile(userId, {
      subscriptionStatus: status,
      lastSubscriptionCheck: Date.now(),
    });
  }

  /**
   * Check if current user has premium access (synchronous, uses cached value)
   */
  checkPremiumAccess(): boolean {
    return hasPremiumAccess(this.cachedProfile);
  }

  /**
   * Check if current user is grandfathered (synchronous, uses cached value)
   */
  isGrandfathered(): boolean {
    return this.cachedProfile?.isGrandfathered ?? false;
  }

  /**
   * Get current subscription status (synchronous, uses cached value)
   */
  getSubscriptionStatus(): SubscriptionStatus | null {
    return this.cachedProfile?.subscriptionStatus ?? null;
  }

  /**
   * Get the cached user profile (synchronous)
   */
  getCachedProfile(): UserProfile | null {
    return this.cachedProfile;
  }

  /**
   * Check if user has seen the premium onboarding flow (synchronous, uses cached value)
   */
  hasSeenPremiumOnboarding(): boolean {
    return this.cachedProfile?.hasSeenPremiumOnboarding ?? false;
  }

  /**
   * Mark the premium onboarding as completed for the current user
   */
  async markPremiumOnboardingComplete(): Promise<void> {
    if (this.userId) {
      await this.updateUserProfile(this.userId, {
        hasSeenPremiumOnboarding: true,
      });
    }
  }
}

